import { z } from "zod";
/**
 * «Обещания» (Паша 04.10.2026: «чтобы я доверял каждой нашей автоматизации, каждому процессу — что мы ничего не
 * теряем, что мы всем отвечаем»): what the Academy's system promises about every person — «каждая заявка стала
 * сделкой за 5 минут», «каждому написавшему ответили», «каждая запись получила памятку» — and who it is not true for
 * right now. The catalog lives outside Vekha (/etc/hypno-robots/promises.yaml); a root timer runs every check
 * (`hypno-promises run --write`, schema 1) and writes the result, Vekha only reads and shows it. Zero — green; a check
 * that could not look — «не проверено», never green.
 */
export const PROMISE_STATES = ["green", "red", "unchecked", "none"];
export const PromiseStateSchema = z.enum(PROMISE_STATES);
export const PROMISE_PRIORITIES = ["P1", "P2", "P3"];
export const PromisePrioritySchema = z.enum(PROMISE_PRIORITIES);
const Text = z.string();
const Moment = z.string().nullable();
/**
 * An exception (Паша 04.10.2026): a case of a red promise marked «не считать» with a reason (a test account and the
 * like) — who marked it and when; it can be taken back. The promises themselves have no exceptions.
 */
export const PromiseExceptionSchema = z.object({
    id: Text,
    reason: Text,
    by: Text.nullable(),
    at: Moment,
}).strict();
/** One person (a deal, a chat, an order) the promise is not kept for, and why. */
export const PromisePersonSchema = z.object({
    key: Text,
    who: Text,
    /** amoCRM deal, GetCourse order — where to look first. */
    link: Text.nullable(),
    /** Vekha's chat of the person, when there is one. */
    chat: Text.nullable(),
    /** What should have happened. */
    expected: Text.nullable(),
    /** What happened instead. */
    actual: Text.nullable(),
    /** Since when it is so (the request, the message, the booking). */
    since: Moment,
    /** Since when the check sees the person. */
    seenSince: Moment,
    /** Whose area: робот, Яна, данные, GetCourse, клиент. */
    zone: Text.nullable(),
    /** Marked in Vekha as an exception: the case does not burn, is not counted and does not alert. */
    exception: PromiseExceptionSchema.nullable(),
}).strict();
export const PROMISE_EXCEPTION_REASON_MAX = 300;
const Key = z.string().trim().min(1).max(300);
export const PromiseExceptionInputSchema = z.object({
    promiseId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,79}$/u),
    caseKey: Key,
    caseLabel: z.string().trim().min(1).max(300),
    reason: z.string().trim().min(2).max(PROMISE_EXCEPTION_REASON_MAX),
}).strict();
export const PromiseExceptionResultSchema = z.object({ promiseId: Text, caseKey: Text, exception: PromiseExceptionSchema.nullable() }).strict();
export const PromiseMetaSchema = z.object({
    /** The accuracy meter's layer behind the promise: N of M at the target. */
    numerator: z.number().nullable(),
    denominator: z.number().nullable(),
    pct: z.number().nullable(),
    target: z.number().nullable(),
    period: Text.nullable(),
    /** When the source the check read was made (the accuracy meter's run, the analytics watcher's check). */
    asOf: Moment,
    /** Who builds a check not made yet. */
    owner: Text.nullable(),
}).strict();
export const PromiseLiveSchema = z.object({
    state: PromiseStateSchema,
    stateSince: Moment,
    /** People the promise is not kept for; null when not checked. */
    count: z.number().int().nonnegative().nullable(),
    /** Why the check could not look. */
    error: Text.nullable(),
    /** When the check last looked successfully. */
    checkedAt: Moment,
    /** The last successful count while the check cannot look now. */
    lastCount: z.number().int().nonnegative().nullable(),
    meta: PromiseMetaSchema,
    people: z.array(PromisePersonSchema),
    /** Cases marked as exceptions: shown, not counted. */
    excepted: z.array(PromisePersonSchema),
}).strict();
export const PromiseCardSchema = z.object({
    id: Text.min(1),
    /** The promise in Паша's words. */
    phrase: Text.min(1),
    priority: PromisePrioritySchema,
    area: Text,
    /** Паша's decision behind it, with the date. */
    source: Text.nullable(),
    /** Whose area by default. */
    zone: Text.nullable(),
    robots: z.array(z.object({ id: Text, name: Text.nullable() }).strict()),
    /** What to do when it is red. */
    action: Text.nullable(),
    /** How it was checked before this page (another watcher, nothing). */
    howBefore: Text.nullable(),
    /** How the check looks, in plain words. */
    checkWords: Text.nullable(),
    window: Text.nullable(),
    note: Text.nullable(),
    live: PromiseLiveSchema,
    issues: z.array(Text),
}).strict();
export const PROMISES_FILE_STATES = ["ok", "stale", "missing", "unreadable", "invalid"];
export const PromisesSummarySchema = z.object({
    total: z.number().int().nonnegative(),
    green: z.number().int().nonnegative(),
    red: z.number().int().nonnegative(),
    unchecked: z.number().int().nonnegative(),
    none: z.number().int().nonnegative(),
    redP1: z.number().int().nonnegative(),
}).strict();
export const PromisesOverviewSchema = z.object({
    /** ok — fresh result; stale — older than 30 minutes; missing / unreadable / invalid — nothing to trust. */
    file: z.enum(PROMISES_FILE_STATES),
    generatedAt: Moment,
    checkedAt: Text,
    areas: z.array(Text),
    summary: PromisesSummarySchema,
    promises: z.array(PromiseCardSchema),
    rejected: z.array(z.object({ index: z.number().int().nonnegative(), id: Text.nullable(), reason: Text }).strict()),
    fileIssues: z.array(Text),
}).strict();
//# sourceMappingURL=promises.js.map